import { useState, useEffect, useRef } from "react";
import { useLanguage, API } from "../App";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Check,
  Sparkles,
  Building2,
  ExternalLink,
  FileText,
  Shield,
  Flag
} from "lucide-react";
import { getCountriesByLanguage } from "../data/translations";
import { trackAnalysisStarted, trackAnalysisStepProgress, trackAnalysisCompleted } from "../utils/analytics";

// ==================== SVG ICONS - PREMIUM SOLID/FILLED STYLE ====================

// Company Size Icons - Premium Solid Style
const IconEmployees1 = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-blue-600">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
  </svg>
);

const IconEmployees10 = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-blue-600">
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
  </svg>
);

const IconEmployees50 = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-blue-600">
    <path d="M12 5.9c1.16 0 2.1.94 2.1 2.1s-.94 2.1-2.1 2.1S9.9 9.16 9.9 8s.94-2.1 2.1-2.1m0 9c2.97 0 6.1 1.46 6.1 2.1v1.1H5.9V17c0-.64 3.13-2.1 6.1-2.1M12 4C9.79 4 8 5.79 8 8s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 9c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4z"/>
    <circle cx="19" cy="8" r="3" opacity="0.6"/>
    <circle cx="5" cy="8" r="3" opacity="0.6"/>
  </svg>
);

const IconEmployees200 = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-blue-600">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
  </svg>
);

// Document Volume Icons - Premium Solid Style
const IconDocs100 = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-emerald-600">
    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
  </svg>
);

const IconDocs500 = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-emerald-600">
    <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3c0 .83-.67 1.5-1.5 1.5h-1V7h1c.83 0 1.5.67 1.5 1.5v1z" opacity="0.9"/>
    <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6z"/>
  </svg>
);

const IconDocs1000 = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-emerald-600">
    <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm2 4v-2H3c0 1.1.89 2 2 2zM3 9h2V7H3v2zm12 12h2v-2h-2v2zm4-18H9c-1.11 0-2 .9-2 2v10c0 1.1.89 2 2 2h10c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 12H9V5h10v10zm-8 6h2v-2h-2v2zm-4 0h2v-2H7v2z"/>
  </svg>
);

const IconDocs5000 = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-emerald-600">
    <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6z"/>
    <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H8V4h12v12z"/>
    <path d="M12 5.5v9l3-2.25L18 14.5v-9z" opacity="0.8"/>
  </svg>
);

// Sector Icons - Premium Solid Style
const SectorIcons = {
  RH: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-violet-600">
      <path d="M12 12.75c1.63 0 3.07.39 4.24.9 1.08.48 1.76 1.56 1.76 2.73V18H6v-1.61c0-1.18.68-2.26 1.76-2.73 1.17-.52 2.61-.91 4.24-.91zM4 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm1.13 1.1c-.37-.06-.74-.1-1.13-.1-.99 0-1.93.21-2.78.58C.48 14.9 0 15.62 0 16.43V18h4.5v-1.61c0-.83.23-1.61.63-2.29zM20 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm4 3.43c0-.81-.48-1.53-1.22-1.85-.85-.37-1.79-.58-2.78-.58-.39 0-.76.04-1.13.1.4.68.63 1.46.63 2.29V18H24v-1.57zM12 6c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z"/>
    </svg>
  ),
  Services: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-amber-600">
      <path d="M21.71 18.29l-4.95-4.95c-.39-.39-1.02-.39-1.41 0l-.71.71-1.06-1.06 4.24-4.24c.39-.39.39-1.02 0-1.41L15.59 5.1c-.39-.39-1.02-.39-1.41 0L9.94 9.34l-1.06-1.06.71-.71c.39-.39.39-1.02 0-1.41L4.64 1.21c-.39-.39-1.02-.39-1.41 0L1.81 2.63c-.39.39-.39 1.02 0 1.41l4.95 4.95c.39.39 1.02.39 1.41 0l.71-.71 1.06 1.06-4.24 4.24c-.39.39-.39 1.02 0 1.41l2.24 2.24c.39.39 1.02.39 1.41 0l4.24-4.24 1.06 1.06-.71.71c-.39.39-.39 1.02 0 1.41l4.95 4.95c.39.39 1.02.39 1.41 0l1.41-1.41c.39-.39.39-1.03 0-1.42z"/>
    </svg>
  ),
  Conseil: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-cyan-600">
      <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2.85 11.1l-.85.6V16h-4v-2.3l-.85-.6C7.8 12.16 7 10.63 7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.63-.8 3.16-2.15 4.1z"/>
    </svg>
  ),
  Education: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-pink-600">
      <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
    </svg>
  ),
  Public: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-indigo-600">
      <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
    </svg>
  ),
  Administration: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-slate-600">
      <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/>
      <path d="M8 15h8v2H8zm0-4h8v2H8z"/>
    </svg>
  ),
  BTP: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-orange-600">
      <path d="M19 12h-3V3H8v9H5l7 7 7-7z"/>
      <path d="M5 21h14v-2H5v2z"/>
    </svg>
  ),
  Technologie: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-blue-600">
      <path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/>
      <path d="M9 9l-2 2 2 2M15 9l2 2-2 2M11 14l2-6" opacity="0.6"/>
    </svg>
  ),
  Tourisme: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-teal-600">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
    </svg>
  ),
  Logistique: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-purple-600">
      <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
    </svg>
  ),
  Finance: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-yellow-600">
      <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
    </svg>
  ),
  Sante: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-red-600">
      <path d="M19 3H5c-1.1 0-1.99.9-1.99 2L3 19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 11h-4v4h-4v-4H6v-4h4V6h4v4h4v4z"/>
    </svg>
  ),
  Industrie: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-stone-600">
      <path d="M22 22H2V10l7-3v2l5-2v3l6-3v2l2-1v14zM12 9.95l-5 2V10l-3 1.32V20h16v-8l-2 1v-3l-6 3v-.05z"/>
      <path d="M6 18h2v-4H6zm4 0h2v-4h-2zm4 0h2v-4h-2z"/>
    </svg>
  ),
  Commerce: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-green-600">
      <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
    </svg>
  )
};

// Document Type Icons - Premium Solid Style
const DocTypeIcons = {
  factures_client: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-blue-600"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>,
  factures_fournisseur: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-violet-600"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>,
  devis: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-amber-600"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14h-2V9h-2V7h4v10z"/></svg>,
  contrats: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-emerald-600"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6zm5-3l-3-3 1.41-1.41L11 14.17l4.59-4.59L17 11l-6 6z"/></svg>,
  legaux: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-indigo-600"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg>,
  bancaires: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-slate-600"><path d="M4 10h3v7H4zm6 0h3v7h-3zm8 10H3v-3h18v3zm-2-10h3v7h-3zM12 1L2 6v2h20V6L12 1z"/></svg>,
  paie: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-green-600"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>,
  certificats: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-yellow-600"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/></svg>,
  livraison: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-purple-600"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>,
  commande: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-orange-600"><path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/></svg>,
  rapports: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-pink-600"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>,
  dossiers: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-cyan-600"><path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/></svg>,
  correspondance: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-teal-600"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>,
  manuels: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-rose-600"><path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z"/></svg>,
  plans: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-lime-600"><path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z"/></svg>,
  rh: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-fuchsia-600"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
};

// Feature Icons - Premium Solid Style
const FeatureIcons = {
  ocr: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-blue-600"><path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg>,
  recherche: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-emerald-600"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>,
  workflows: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-violet-600"><path d="M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3h7zM7 9H4V5h3v4zm10 6h3v4h-3v-4zm0-10h3v4h-3V5z"/></svg>,
  securite: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-red-600"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/></svg>,
  erp: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-amber-600"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>,
  mobile: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-cyan-600"><path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/></svg>,
  analytics: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-pink-600"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>,
  coffre: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-slate-600"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>,
  collaboration: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-green-600"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>,
  automatisation: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-purple-600"><path d="M19.03 7.39l1.42-1.42c-.43-.51-.9-.99-1.41-1.41l-1.42 1.42C16.07 4.74 14.12 4 12 4c-4.97 0-9 4.03-9 9s4.02 9 9 9 9-4.03 9-9c0-2.12-.74-4.07-1.97-5.61zM13 14h-2V7h2v7z"/></svg>
};

// Deployment Icons - Premium Solid Style
const DeploymentIcons = {
  cloud: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-blue-600"><path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/></svg>,
  onpremise: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-slate-600"><path d="M2 20h20v-4H2v4zm2-3h2v2H4v-2zM2 4v4h20V4H2zm4 3H4V5h2v2zm-4 7h20v-4H2v4zm2-3h2v2H4v-2z"/></svg>,
  hybride: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-indigo-600"><path d="M19.35 10.04C18.67 6.59 15.64 4 12 4c-1.48 0-2.85.43-4.01 1.17l1.46 1.46C10.21 6.23 11.08 6 12 6c3.04 0 5.5 2.46 5.5 5.5v.5H19c1.66 0 3 1.34 3 3 0 1.13-.64 2.11-1.56 2.62l1.45 1.45C23.16 18.16 24 16.68 24 15c0-2.64-2.05-4.78-4.65-4.96zM3 5.27l2.75 2.74C2.56 8.15 0 10.77 0 14c0 3.31 2.69 6 6 6h11.73l2 2L21 20.73 4.27 4 3 5.27zM7.73 10l8 8H6c-2.21 0-4-1.79-4-4s1.79-4 4-4h1.73z"/></svg>
};

// Priority Icons - Premium Solid Style
const PriorityIcons = {
  integration: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-blue-600"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14h-2V9h-2V7h4v10z"/></svg>,
  fonctionnalites: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-amber-600"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>,
  facilite: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-green-600"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>,
  securite: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-indigo-600"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>,
  eco: () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-emerald-600"><path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 4.12 13.38 3 12 3S9.5 4.12 9.5 5.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25zM12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zM3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/></svg>
};

// Country Flags
const CountryFlags = {
  FR: () => <svg viewBox="0 0 60 40" className="w-full h-full"><rect width="20" height="40" fill="#002654"/><rect x="20" width="20" height="40" fill="#FFFFFF"/><rect x="40" width="20" height="40" fill="#CE1126"/></svg>,
  BE: () => <svg viewBox="0 0 60 40" className="w-full h-full"><rect width="20" height="40" fill="#000000"/><rect x="20" width="20" height="40" fill="#FAE042"/><rect x="40" width="20" height="40" fill="#ED2939"/></svg>,
  CH: () => <svg viewBox="0 0 60 40" className="w-full h-full"><rect width="60" height="40" fill="#FF0000"/><rect x="24" y="8" width="12" height="24" fill="#FFFFFF"/><rect x="18" y="14" width="24" height="12" fill="#FFFFFF"/></svg>,
  LU: () => <svg viewBox="0 0 60 40" className="w-full h-full"><rect width="60" height="13.33" fill="#ED2939"/><rect y="13.33" width="60" height="13.33" fill="#FFFFFF"/><rect y="26.66" width="60" height="13.33" fill="#00A1DE"/></svg>,
  ES: () => <svg viewBox="0 0 60 40" className="w-full h-full"><rect width="60" height="10" fill="#AA151B"/><rect y="10" width="60" height="20" fill="#F1BF00"/><rect y="30" width="60" height="10" fill="#AA151B"/></svg>,
  IT: () => <svg viewBox="0 0 60 40" className="w-full h-full"><rect width="20" height="40" fill="#009246"/><rect x="20" width="20" height="40" fill="#FFFFFF"/><rect x="40" width="20" height="40" fill="#CE2B37"/></svg>,
  PT: () => <svg viewBox="0 0 60 40" className="w-full h-full"><rect width="24" height="40" fill="#006600"/><rect x="24" width="36" height="40" fill="#FF0000"/><circle cx="24" cy="20" r="8" fill="#FFCC00"/></svg>
};

// ==================== MAIN COMPONENT ====================

const SelectionPad = ({ icon: Icon, label, selected, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all min-w-[90px] ${
      selected 
        ? "border-blue-500 bg-blue-50 shadow-md" 
        : disabled 
          ? "border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed"
          : "border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/30"
    }`}
  >
    <Icon />
    <span className={`text-xs font-medium text-center leading-tight ${selected ? "text-blue-700" : "text-slate-600"}`}>
      {label}
    </span>
  </button>
);

const ProgressIndicator = ({ currentStep, steps, t }) => (
  <div className="mb-8">
    <div className="flex items-center justify-between mb-4">
      {steps.map((step, index) => (
        <div key={index} className="flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm transition-all ${
            index <= currentStep 
              ? "bg-blue-600 text-white shadow-lg" 
              : "bg-slate-200 text-slate-500"
          }`}>
            {index < currentStep ? <Check size={18} /> : index + 1}
          </div>
          <span className={`ml-2 text-sm font-medium hidden sm:block ${
            index <= currentStep ? "text-blue-600" : "text-slate-400"
          }`}>
            {step}
          </span>
          {index < steps.length - 1 && (
            <div className={`w-12 sm:w-24 h-1 mx-2 rounded ${
              index < currentStep ? "bg-blue-600" : "bg-slate-200"
            }`} />
          )}
        </div>
      ))}
    </div>
  </div>
);

const LoadingAnimation = ({ t }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="flex flex-col items-center justify-center py-16"
  >
    <div className="relative w-24 h-24 mb-8">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0 border-4 border-blue-200 rounded-full"
      />
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        className="absolute inset-2 border-4 border-t-blue-600 border-r-transparent border-b-transparent border-l-transparent rounded-full"
      />
      <motion.div
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 1, repeat: Infinity }}
        className="absolute inset-0 flex items-center justify-center"
      >
        <Sparkles className="text-blue-600" size={32} />
      </motion.div>
    </div>
    <motion.p
      animate={{ opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 1.5, repeat: Infinity }}
      className="text-lg font-medium text-slate-600"
    >
      {t.results.loading}
    </motion.p>
  </motion.div>
);

const ResultCard = ({ solution, rank, t, language, onLearnMore }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: rank * 0.2 }}
    className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-slate-100 flex flex-col h-full"
  >
    {/* Header with gradient */}
    <div 
      className="p-4 relative overflow-hidden"
      style={{ 
        background: `linear-gradient(135deg, ${solution.color}15 0%, ${solution.color}05 100%)`,
        borderBottom: `3px solid ${solution.color}`
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {rank === 0 && (
            <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full shadow-sm">
              🏆 {language === "fr" ? "Meilleur choix" : language === "es" ? "Mejor opción" : "Best match"}
            </span>
          )}
          {rank === 1 && (
            <span className="bg-slate-200 text-slate-700 text-xs font-semibold px-2 py-1 rounded-full">
              #2
            </span>
          )}
          {rank === 2 && (
            <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-1 rounded-full">
              #3
            </span>
          )}
        </div>
        <div className="text-right">
          <div className="text-4xl font-black" style={{ color: solution.color }}>
            {solution.match_score}%
          </div>
          <span className="text-xs text-slate-500 font-medium">{t.results.match}</span>
        </div>
      </div>
      <h3 className="font-bold text-2xl mt-3" style={{ color: solution.color }}>
        {solution.name}
      </h3>
    </div>
    
    <div className="p-5 flex flex-col flex-grow">
      {/* AI Reason - personalized recommendation */}
      {solution.ai_reason && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-3 mb-4">
          <div className="flex items-start gap-2">
            <Sparkles size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800 leading-relaxed">{solution.ai_reason}</p>
          </div>
        </div>
      )}
      
      <p className="text-slate-600 text-sm mb-4 leading-relaxed">{solution.description}</p>
      
      <div className="space-y-4 flex-grow flex flex-col">
        <div>
          <p className="text-xs text-slate-500 mb-2 font-medium uppercase tracking-wide">{t.results.features}</p>
          <div className="flex flex-wrap gap-1.5">
            {solution.features.map((f, i) => (
              <Badge key={i} variant="secondary" className="text-xs bg-slate-100 hover:bg-slate-200 transition-colors">{f}</Badge>
            ))}
          </div>
        </div>
        
        <div className="flex justify-between pt-4 border-t border-slate-100">
          <div>
            <p className="text-xs text-slate-500 font-medium">{t.results.deployment}</p>
            <p className="text-sm font-semibold text-slate-700">{solution.deployment.join(", ")}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 font-medium">{t.results.pricing}</p>
            <p className="text-xl font-bold" style={{ color: solution.color }}>{solution.pricing}</p>
          </div>
        </div>
        
        {/* Spacer to push button to bottom */}
        <div className="flex-grow" />
        
        {/* Learn More Button - always at bottom */}
        <Button 
          onClick={() => onLearnMore(solution)}
          className="w-full mt-auto transition-all duration-200 hover:scale-[1.02]"
          style={{ backgroundColor: solution.color }}
        >
          <ExternalLink size={16} className="mr-2" />
          {language === "fr" ? "En savoir plus" : language === "es" ? "Saber más" : "Learn more"}
        </Button>
      </div>
    </div>
  </motion.div>
);

export const AIAnalyzer = ({ isVisible, onStart }) => {
  const { language, t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const analyzerRef = useRef(null);

  // Scroll to top of analyzer when step changes + GA4 funnel tracking
  useEffect(() => {
    if (analyzerRef.current && currentStep > 0) {
      analyzerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // GA4: step progress event on every transition
    trackAnalysisStepProgress(currentStep + 1, `step_${currentStep + 1}`);
  }, [currentStep]);

  // GA4: fire "analysis_started" once when analyzer becomes visible
  useEffect(() => {
    if (isVisible) {
      trackAnalysisStarted();
    }
  }, [isVisible]);

  const [formData, setFormData] = useState({
    companySize: "",
    docVolume: "",
    sector: "",
    docTypes: [],
    features: [],
    deployment: "",
    priorities: [],
    certifications: [],
    country: ""
  });

  const steps = [t.analyzer.step1, t.analyzer.step2, t.analyzer.step3];

  const companySizes = [
    { value: "1-10", label: "1-10", icon: IconEmployees1 },
    { value: "11-50", label: "11-50", icon: IconEmployees10 },
    { value: "51-200", label: "51-200", icon: IconEmployees50 },
    { value: "+201", label: "+201", icon: IconEmployees200 }
  ];

  const docVolumes = [
    { value: "0-100", label: "0-100", icon: IconDocs100 },
    { value: "100-500", label: "100-500", icon: IconDocs500 },
    { value: "500-800", label: "500-800", icon: IconDocs500 },
    { value: "800-1000", label: "800-1000", icon: IconDocs1000 },
    { value: "1000-2500", label: "1000-2500", icon: IconDocs1000 },
    { value: "3000-5000", label: "3000-5000", icon: IconDocs5000 }
  ];

  // Translated sector labels
  const sectorLabels = {
    fr: { RH: "RH", Services: "Services", Conseil: "Conseil", Education: "Éducation", Public: "Secteur public", Administration: "Administration", BTP: "BTP", Technologie: "Technologie", Tourisme: "Tourisme", Logistique: "Logistique", Finance: "Finance", Sante: "Santé", Industrie: "Industrie", Commerce: "Commerce" },
    es: { RH: "RRHH", Services: "Servicios", Conseil: "Consultoría", Education: "Educación", Public: "Sector público", Administration: "Administración", BTP: "Construcción", Technologie: "Tecnología", Tourisme: "Turismo", Logistique: "Logística", Finance: "Finanzas", Sante: "Salud", Industrie: "Industria", Commerce: "Comercio" },
    en: { RH: "HR", Services: "Services", Conseil: "Consulting", Education: "Education", Public: "Public Sector", Administration: "Administration", BTP: "Construction", Technologie: "Technology", Tourisme: "Tourism", Logistique: "Logistics", Finance: "Finance", Sante: "Healthcare", Industrie: "Industry", Commerce: "Retail" }
  };
  const sl = sectorLabels[language] || sectorLabels.fr;

  const sectors = [
    { value: "RH", label: sl.RH, icon: SectorIcons.RH },
    { value: "Services", label: sl.Services, icon: SectorIcons.Services },
    { value: "Conseil", label: sl.Conseil, icon: SectorIcons.Conseil },
    { value: "Éducation", label: sl.Education, icon: SectorIcons.Education },
    { value: "Secteur public", label: sl.Public, icon: SectorIcons.Public },
    { value: "Administration", label: sl.Administration, icon: SectorIcons.Administration },
    { value: "BTP", label: sl.BTP, icon: SectorIcons.BTP },
    { value: "Technologie", label: sl.Technologie, icon: SectorIcons.Technologie },
    { value: "Tourisme", label: sl.Tourisme, icon: SectorIcons.Tourisme },
    { value: "Logistique", label: sl.Logistique, icon: SectorIcons.Logistique },
    { value: "Finance", label: sl.Finance, icon: SectorIcons.Finance },
    { value: "Santé", label: sl.Sante, icon: SectorIcons.Sante },
    { value: "Industrie", label: sl.Industrie, icon: SectorIcons.Industrie },
    { value: "Commerce", label: sl.Commerce, icon: SectorIcons.Commerce }
  ];

  // Translated document types
  const docTypeLabels = {
    fr: { factures_client: "Factures client", factures_fournisseur: "Factures fournisseur", devis: "Devis", contrats: "Contrats", legaux: "Documents légaux", bancaires: "Documents bancaires", paie: "Fiches de paie", certificats: "Certificats", livraison: "Bons de livraison", commande: "Bons de commande", rapports: "Rapports", dossiers: "Dossiers", correspondance: "Correspondance", manuels: "Manuels techniques", plans: "Plans", rh: "Documents RH" },
    es: { factures_client: "Facturas cliente", factures_fournisseur: "Facturas proveedor", devis: "Presupuestos", contrats: "Contratos", legaux: "Documentos legales", bancaires: "Documentos bancarios", paie: "Nóminas", certificats: "Certificados", livraison: "Albaranes", commande: "Pedidos", rapports: "Informes", dossiers: "Expedientes", correspondance: "Correspondencia", manuels: "Manuales técnicos", plans: "Planos", rh: "Documentos RRHH" },
    en: { factures_client: "Customer invoices", factures_fournisseur: "Supplier invoices", devis: "Quotes", contrats: "Contracts", legaux: "Legal documents", bancaires: "Bank documents", paie: "Payslips", certificats: "Certificates", livraison: "Delivery notes", commande: "Purchase orders", rapports: "Reports", dossiers: "Files", correspondance: "Correspondence", manuels: "Technical manuals", plans: "Plans", rh: "HR Documents" }
  };
  const dl = docTypeLabels[language] || docTypeLabels.fr;

  const docTypes = [
    { value: dl.factures_client, icon: DocTypeIcons.factures_client },
    { value: dl.factures_fournisseur, icon: DocTypeIcons.factures_fournisseur },
    { value: dl.devis, icon: DocTypeIcons.devis },
    { value: dl.contrats, icon: DocTypeIcons.contrats },
    { value: dl.legaux, icon: DocTypeIcons.legaux },
    { value: dl.bancaires, icon: DocTypeIcons.bancaires },
    { value: dl.paie, icon: DocTypeIcons.paie },
    { value: dl.certificats, icon: DocTypeIcons.certificats },
    { value: dl.livraison, icon: DocTypeIcons.livraison },
    { value: dl.commande, icon: DocTypeIcons.commande },
    { value: dl.rapports, icon: DocTypeIcons.rapports },
    { value: dl.dossiers, icon: DocTypeIcons.dossiers },
    { value: dl.correspondance, icon: DocTypeIcons.correspondance },
    { value: dl.manuels, icon: DocTypeIcons.manuels },
    { value: dl.plans, icon: DocTypeIcons.plans },
    { value: dl.rh, icon: DocTypeIcons.rh }
  ];

  // Translated features
  const featureLabels = {
    fr: { ocr: "Reconnaissance automatique", recherche: "Recherche avancée", workflows: "Workflows automatisés", securite: "Sécurité avancée", erp: "Intégration ERP/CRM", mobile: "Accès mobile", analytics: "Analyses et rapports", coffre: "Coffre-fort électronique", collaboration: "Collaboration temps réel", automatisation: "Automatisation processus" },
    es: { ocr: "Reconocimiento automático", recherche: "Búsqueda avanzada", workflows: "Flujos automatizados", securite: "Seguridad avanzada", erp: "Integración ERP/CRM", mobile: "Acceso móvil", analytics: "Análisis e informes", coffre: "Caja fuerte electrónica", collaboration: "Colaboración en tiempo real", automatisation: "Automatización procesos" },
    en: { ocr: "Automatic recognition", recherche: "Advanced Search", workflows: "Automated Workflows", securite: "Advanced Security", erp: "ERP/CRM Integration", mobile: "Mobile Access", analytics: "Analytics & Reports", coffre: "Electronic Vault", collaboration: "Real-time Collaboration", automatisation: "Process Automation" }
  };
  const fl = featureLabels[language] || featureLabels.fr;

  const features = [
    { value: fl.ocr, icon: FeatureIcons.ocr },
    { value: fl.recherche, icon: FeatureIcons.recherche },
    { value: fl.workflows, icon: FeatureIcons.workflows },
    { value: fl.securite, icon: FeatureIcons.securite },
    { value: fl.erp, icon: FeatureIcons.erp },
    { value: fl.mobile, icon: FeatureIcons.mobile },
    { value: fl.analytics, icon: FeatureIcons.analytics },
    { value: fl.coffre, icon: FeatureIcons.coffre },
    { value: fl.collaboration, icon: FeatureIcons.collaboration },
    { value: fl.automatisation, icon: FeatureIcons.automatisation }
  ];

  // Translated deployments
  const deploymentLabels = {
    fr: { cloud: "Cloud - Hébergé dans le cloud", onpremise: "On-premise - Sur site", hybride: "Hybride - Combinaison cloud/local" },
    es: { cloud: "Cloud - Alojado en la nube", onpremise: "On-premise - En instalaciones", hybride: "Híbrido - Combinación cloud/local" },
    en: { cloud: "Cloud - Cloud hosted", onpremise: "On-premise - Local servers", hybride: "Hybrid - Cloud/local combination" }
  };
  const dpl = deploymentLabels[language] || deploymentLabels.fr;

  const deployments = [
    { value: "Cloud", label: dpl.cloud, icon: DeploymentIcons.cloud },
    { value: "On-premise", label: dpl.onpremise, icon: DeploymentIcons.onpremise },
    { value: "Hybride", label: dpl.hybride, icon: DeploymentIcons.hybride }
  ];

  // Translated priorities
  const priorityLabels = {
    fr: { integration: "Intégration facile", fonctionnalites: "Fonctionnalités", facilite: "Facilité d'usage", securite: "Sécurité", eco: "Eco-Friendly" },
    es: { integration: "Integración fácil", fonctionnalites: "Funcionalidades", facilite: "Facilidad de uso", securite: "Seguridad", eco: "Eco-Friendly" },
    en: { integration: "Easy Integration", fonctionnalites: "Features", facilite: "Ease of Use", securite: "Security", eco: "Eco-Friendly" }
  };
  const pl = priorityLabels[language] || priorityLabels.fr;

  const priorities = [
    { value: pl.integration, icon: PriorityIcons.integration },
    { value: pl.fonctionnalites, icon: PriorityIcons.fonctionnalites },
    { value: pl.facilite, icon: PriorityIcons.facilite },
    { value: pl.securite, icon: PriorityIcons.securite },
    { value: pl.eco, icon: PriorityIcons.eco }
  ];

  const availableCountries = getCountriesByLanguage(language);

  const countryNames = {
    FR: "France", BE: "Belgique", CH: "Suisse", LU: "Luxembourg",
    ES: "España", IT: "Italia", PT: "Portugal"
  };

  const countryCerts = {
    FR: ["ISO/IEC 27001 / 17 / 18 / 14641", "RGPD", "HDS", "NF 461", "eIDAS", "Factur-X", "Chorus Pro"],
    BE: ["ISO/IEC 27001", "RGPD", "HDS Belgique", "NBN", "eIDAS", "Peppol BIS Billing 3.0"],
    CH: ["ISO/IEC 27001", "LPD", "SS 584:2020", "HDS Suisse", "ZertES"],
    LU: ["ISO/IEC 27001", "RGPD", "HDS Luxembourg", "LexArchive", "eIDAS", "LuxTrust", "eCDF"],
    ES: ["ISO/IEC 27001", "RGPD", "ENS", "Ley Crea y Crece", "VeriFactu", "SII IVA", "Facturae", "eIDAS"],
    IT: ["ISO/IEC 27001 / 17 / 18", "RGPD", "AgID", "eIDAS", "SPID", "FatturaPA", "SDI"],
    PT: ["ISO/IEC 27001 / 17 / 18", "RGPD", "SPMS", "RSE", "eIDAS", "SAF-T"]
  };

  const handleFlagClick = (country) => {
    setSelectedCountry(country);
    setCertDialogOpen(true);
  };

  const handleCertToggle = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert],
      country: selectedCountry
    }));
  };

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    try {
      const response = await axios.post(`${API}/analyze`, {
        company_size: formData.companySize,
        doc_volume: formData.docVolume,
        sector: formData.sector,
        doc_types: formData.docTypes,
        features: formData.features,
        deployment: formData.deployment,
        priorities: formData.priorities,
        certifications: formData.certifications,
        country: formData.country,
        language
      });
      
      // Quick transition animation
      await new Promise(resolve => setTimeout(resolve, 600));
      setResults(response.data.recommendations);
      // GA4 CONVERSION event
      trackAnalysisCompleted(response.data.recommendations?.length || 3, language);
      toast.success(language === "fr" ? "Analyse terminée !" : language === "es" ? "¡Análisis completado!" : "Analysis complete!");
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Erreur lors de l'analyse");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const resetAnalysis = () => {
    setResults(null);
    setCurrentStep(0);
    setFormData({
      companySize: "", docVolume: "", sector: "", docTypes: [],
      features: [], deployment: "", priorities: [], certifications: [], country: ""
    });
  };

  const canProceed = () => {
    if (currentStep === 0) return formData.companySize && formData.docVolume && formData.sector;
    if (currentStep === 1) return formData.docTypes.length > 0 && formData.features.length >= 5 && formData.deployment;
    return true;
  };

  if (!isVisible && !results) {
    const ctaButtonLabels = {
      fr: "Démarrer l'analyse IA",
      es: "Iniciar análisis IA",
      en: "Start AI Analysis"
    };
    
    return (
      <div className="max-w-4xl mx-auto text-center">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="relative rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Video Background */}
          <div className="absolute inset-0 z-0">
            <video 
              autoPlay 
              loop 
              muted 
              playsInline
              className="w-full h-full object-cover opacity-30"
            >
              <source src="https://videos.pexels.com/video-files/3129671/3129671-uhd_2560_1440_30fps.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-blue-900/80 to-slate-900/90" />
          </div>
          
          {/* Content */}
          <div className="relative z-10 p-8 md:p-12">
            <div className="p-4 bg-blue-500/20 backdrop-blur-sm rounded-2xl w-20 h-20 mx-auto mb-6 flex items-center justify-center border border-blue-400/30">
              <Sparkles size={40} className="text-blue-400" />
            </div>
            <h2 className="font-bold text-3xl text-white mb-4" style={{ fontFamily: 'Arial' }}>
              {t.analyzer.title}
            </h2>
            <p className="text-blue-100/80 mb-8 max-w-lg mx-auto">{t.hero.info}</p>
            
            {/* Blinking CTA Button - Centered on all screens */}
            <div className="flex justify-center">
              <Button 
                onClick={onStart} 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-6 md:px-8 py-5 md:py-6 text-base md:text-lg animate-pulse shadow-lg shadow-blue-600/50 hover:shadow-blue-500/50 transition-all duration-300"
                data-testid="start-analyzer-btn"
              >
                <Sparkles size={20} className="mr-2 md:mr-3" />
                {ctaButtonLabels[language] || ctaButtonLabels.fr}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (isAnalyzing) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <LoadingAnimation t={t} />
        </div>
      </div>
    );
  }

  if (results) {
    const globalInsight = results[0]?.ai_insight;
    const newAnalysisLabels = {
      fr: "Nouvelle analyse",
      es: "Hacer nuevo análisis", 
      en: "New analysis"
    };
    return (
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center mb-8">
          <h2 className="font-bold text-3xl md:text-4xl text-slate-900 mb-4">{t.results.title}</h2>
          <Button variant="outline" onClick={resetAnalysis}>
            <Sparkles size={18} className="mr-2" />
            {newAnalysisLabels[language] || newAnalysisLabels.fr}
          </Button>
        </motion.div>
        
        {/* AI Global Insight */}
        {globalInsight && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 mb-8"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Sparkles size={24} className="text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">
                  {language === "fr" ? "Analyse IA personnalisée" : language === "es" ? "Análisis IA personalizado" : "Personalized AI Analysis"}
                </h3>
                <p className="text-blue-800">{globalInsight}</p>
              </div>
            </div>
          </motion.div>
        )}
        
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {results.map((solution, index) => (
            <ResultCard 
              key={solution.id} 
              solution={solution} 
              rank={index} 
              t={t} 
              language={language}
              onLearnMore={(sol) => {
                // Open GED Solutions section with this solution highlighted
                const gedSection = document.getElementById('ged-solutions');
                if (gedSection) {
                  gedSection.scrollIntoView({ behavior: 'smooth' });
                  // Try to click on the solution card
                  setTimeout(() => {
                    const card = document.querySelector(`[data-testid="solution-card-${sol.id}"]`);
                    if (card) card.click();
                  }, 500);
                }
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={analyzerRef} className="max-w-5xl mx-auto" data-testid="ai-analyzer">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6">
          <h2 className="font-bold text-2xl mb-6" style={{ fontFamily: 'Arial' }}>{t.analyzer.title}</h2>
          <ProgressIndicator currentStep={currentStep} steps={steps} t={t} />
        </div>

        <div className="p-6 md:p-8">
          <AnimatePresence mode="wait">
            {/* STEP 1 */}
            {currentStep === 0 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
                <div className="text-center mb-6">
                  <Building2 size={32} className="text-blue-600 mx-auto mb-2" />
                  <h3 className="text-lg font-semibold text-slate-800">
                    {language === "fr" ? "Parlez-nous de votre entreprise" : language === "es" ? "Háblenos de su empresa" : "Tell us about your company"}
                  </h3>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-800 mb-4">
                    {language === "fr" ? "Quelle est la taille de l'entreprise ?" : language === "es" ? "¿Cuál es el tamaño de su empresa?" : "What is your company size?"} *
                  </h4>
                  <div className="flex flex-wrap gap-3 justify-center">
                    {companySizes.map((size) => (
                      <SelectionPad key={size.value} icon={size.icon} label={size.label} selected={formData.companySize === size.value}
                        onClick={() => setFormData(prev => ({ ...prev, companySize: size.value }))} />
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-800 mb-4">
                    {language === "fr" ? "Combien de documents gérez-vous par mois ?" : language === "es" ? "¿Cuántos documentos gestiona al mes?" : "How many documents do you manage per month?"} *
                  </h4>
                  <div className="flex flex-wrap gap-3 justify-center">
                    {docVolumes.map((vol) => (
                      <SelectionPad key={vol.value} icon={vol.icon} label={vol.label} selected={formData.docVolume === vol.value}
                        onClick={() => setFormData(prev => ({ ...prev, docVolume: vol.value }))} />
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-800 mb-4">
                    {language === "fr" ? "Quel est votre secteur d'activité ?" : language === "es" ? "¿Cuál es su sector de actividad?" : "What is your business sector?"} *
                  </h4>
                  <div className="flex flex-wrap gap-3 justify-center">
                    {sectors.map((sector) => (
                      <SelectionPad key={sector.value} icon={sector.icon} label={sector.label} selected={formData.sector === sector.value}
                        onClick={() => setFormData(prev => ({ ...prev, sector: sector.value }))} />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP 2 */}
            {currentStep === 1 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
                <div className="text-center mb-6">
                  <FileText size={32} className="text-green-600 mx-auto mb-2" />
                  <h3 className="text-lg font-semibold text-slate-800">
                    {language === "fr" ? "Documents et besoins" : language === "es" ? "Documentos y necesidades" : "Documents & Needs"}
                  </h3>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-800 mb-4">
                    {language === "fr" ? "Quel type de documents traitez-vous ?" : language === "es" ? "¿Qué tipo de documentos gestiona?" : "What types of documents do you manage?"}
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {docTypes.map((doc) => (
                      <SelectionPad key={doc.value} icon={doc.icon} label={doc.value} selected={formData.docTypes.includes(doc.value)}
                        onClick={() => setFormData(prev => ({
                          ...prev, docTypes: prev.docTypes.includes(doc.value) 
                            ? prev.docTypes.filter(d => d !== doc.value) 
                            : [...prev.docTypes, doc.value]
                        }))} />
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    {language === "fr" ? "Quelles sont vos fonctionnalités prioritaires ?" : language === "es" ? "¿Cuáles son sus funcionalidades prioritarias?" : "What are your priority features?"} *
                    <Badge variant={formData.features.length >= 5 ? "default" : "destructive"} className="ml-2">
                      {formData.features.length}/5 min
                    </Badge>
                  </h4>
                  <div className="flex flex-wrap gap-3 justify-center">
                    {features.map((feat) => (
                      <SelectionPad key={feat.value} icon={feat.icon} label={feat.value} selected={formData.features.includes(feat.value)}
                        onClick={() => setFormData(prev => ({
                          ...prev, features: prev.features.includes(feat.value) 
                            ? prev.features.filter(f => f !== feat.value) 
                            : [...prev.features, feat.value]
                        }))} />
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-800 mb-4">
                    {language === "fr" ? "Quel est votre type de déploiement ?" : language === "es" ? "¿Cuál es su tipo de despliegue?" : "What is your deployment type?"} *
                  </h4>
                  <div className="flex flex-wrap gap-4 justify-center">
                    {deployments.map((dep) => (
                      <SelectionPad key={dep.value} icon={dep.icon} label={dep.label} selected={formData.deployment === dep.value}
                        onClick={() => setFormData(prev => ({ ...prev, deployment: dep.value }))} />
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-800 mb-4">
                    {language === "fr" ? "Quelle est votre priorité principale ?" : language === "es" ? "¿Cuál es su prioridad principal?" : "What is your main priority?"}
                  </h4>
                  <div className="flex flex-wrap gap-3 justify-center">
                    {priorities.map((pri) => (
                      <SelectionPad key={pri.value} icon={pri.icon} label={pri.value} selected={formData.priorities.includes(pri.value)}
                        onClick={() => setFormData(prev => ({
                          ...prev, priorities: prev.priorities.includes(pri.value) 
                            ? prev.priorities.filter(p => p !== pri.value) 
                            : [...prev.priorities, pri.value]
                        }))} />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP 3 */}
            {currentStep === 2 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
                <div className="text-center mb-6">
                  <Flag size={32} className="text-purple-600 mx-auto mb-2" />
                  <h3 className="text-lg font-semibold text-slate-800">
                    {language === "fr" ? "Quelles sont les certifications requises ?" : language === "es" ? "¿Qué certificaciones necesita?" : "Required certifications"}
                  </h3>
                  <p className="text-slate-500 text-sm mt-2">
                    {language === "fr" ? "Cliquez sur un drapeau pour sélectionner les certifications" : "Click a flag to select certifications"}
                  </p>
                </div>

                <div className="flex flex-wrap gap-6 justify-center">
                  {availableCountries.map((code) => (
                    <button key={code} onClick={() => handleFlagClick(code)}
                      className={`w-20 h-14 rounded-xl overflow-hidden border-3 transition-all hover:scale-105 shadow-md ${
                        formData.country === code ? "border-blue-500 ring-4 ring-blue-200" : "border-slate-200 hover:border-blue-300"
                      }`} data-testid={`flag-${code}`}>
                      {CountryFlags[code]()}
                    </button>
                  ))}
                </div>

                {formData.certifications.length > 0 && (
                  <div className="bg-green-50 rounded-xl p-4">
                    <p className="text-sm font-medium text-green-800 mb-2">
                      {language === "fr" ? "Certifications sélectionnées:" : "Selected certifications:"}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {formData.certifications.map((cert) => (
                        <Badge key={cert} className="bg-green-600">{cert}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <Dialog open={certDialogOpen} onOpenChange={setCertDialogOpen}>
                  <DialogContent className="bg-white border-slate-200" data-testid="certification-dialog">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-3 text-slate-900">
                        <div className="w-10 h-7 rounded overflow-hidden">
                          {selectedCountry && CountryFlags[selectedCountry]()}
                        </div>
                        {selectedCountry && countryNames[selectedCountry]}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                      <p className="text-sm text-slate-600 mb-4">
                        {language === "fr" ? "Sélectionnez les certifications requises:" : language === "es" ? "Seleccione las certificaciones requeridas:" : "Select required certifications:"}
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {selectedCountry && countryCerts[selectedCountry]?.map((cert) => (
                          <button key={cert} onClick={() => handleCertToggle(cert)}
                            data-testid={`cert-${cert}`}
                            className={`px-4 py-2 rounded-lg border-2 font-medium transition-all ${
                              formData.certifications.includes(cert)
                                ? "border-green-500 bg-green-50 text-green-700"
                                : "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50"
                            }`}>
                            {formData.certifications.includes(cert) && <Check size={16} className="inline mr-2" />}
                            {cert}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Button onClick={() => setCertDialogOpen(false)} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                      {language === "fr" ? "Valider" : language === "es" ? "Validar" : "Confirm"}
                    </Button>
                  </DialogContent>
                </Dialog>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="border-t p-6 flex justify-between">
          <Button variant="outline" onClick={() => setCurrentStep(prev => prev - 1)} disabled={currentStep === 0}>
            <ArrowLeft size={18} className="mr-2" />{language === "fr" ? "Retour" : t.analyzer.back}
          </Button>
          {currentStep < 2 ? (
            <Button onClick={() => setCurrentStep(prev => prev + 1)} disabled={!canProceed()} className="bg-blue-600 hover:bg-blue-700">
              {t.analyzer.next}<ArrowRight size={18} className="ml-2" />
            </Button>
          ) : (
            <Button onClick={handleAnalyze} className="bg-blue-600 hover:bg-blue-700 animate-pulse-glow">
              <Sparkles size={18} className="mr-2" />{t.analyzer.analyze}
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AIAnalyzer;